import React from 'react';
import { ExclamationTriangleIcon, XCircleIcon } from '@heroicons/react/24/outline';

/**
 * UsageMeter - Reusable usage bar showing current/limit with warning thresholds.
 *
 * Thresholds:
 *   <70%  - green (healthy)
 *   70-89% - amber (warning)
 *   90-99% - red (critical)
 *   100%  - red with "Limit reached" badge
 *
 * @param {string} label - What is being measured (e.g., "Exports", "Stores")
 * @param {number} used - Current usage count
 * @param {number} limit - Maximum allowed (-1 = unlimited)
 * @param {string} unit - Optional unit label (e.g., "exports", "stores")
 * @param {boolean} showWarning - Show warning text at 70%+ (default: true)
 * @param {string} size - 'small' | 'default' | 'large'
 * @param {string} className - Additional CSS classes
 */
const UsageMeter = ({
  label,
  used = 0,
  limit = -1,
  unit = '',
  showWarning = true,
  size = 'default',
  className = '',
}) => {
  const isUnlimited = limit === -1;
  const percentage = isUnlimited ? 0 : limit > 0 ? Math.min((used / limit) * 100, 100) : 0;
  const remaining = isUnlimited ? Infinity : Math.max(0, limit - used);
  const isAtLimit = !isUnlimited && used >= limit;

  const getBarColor = () => {
    if (percentage >= 100) return 'bg-red-500';
    if (percentage >= 90) return 'bg-red-400';
    if (percentage >= 70) return 'bg-amber-400';
    return 'bg-emerald-500';
  };

  const getBarBg = () => {
    if (percentage >= 90) return 'bg-red-100';
    if (percentage >= 70) return 'bg-amber-100';
    return 'bg-sage-100';
  };

  const getTextColor = () => {
    if (percentage >= 90) return 'text-red-600';
    if (percentage >= 70) return 'text-amber-600';
    return 'text-sage-600';
  };

  const barHeight =
    {
      small: 'h-1',
      default: 'h-2',
      large: 'h-3',
    }[size] || 'h-2';

  const textSize =
    {
      small: 'text-[10px]',
      default: 'text-xs',
      large: 'text-sm',
    }[size] || 'text-xs';

  const labelSize =
    {
      small: 'text-[10px]',
      default: 'text-xs',
      large: 'text-sm',
    }[size] || 'text-xs';

  return (
    <div className={className}>
      {/* Header row */}
      <div className="flex items-center justify-between mb-1">
        <span className={`font-medium text-sage-700 ${labelSize}`}>{label}</span>
        <div className="flex items-center space-x-1.5">
          {isAtLimit && <XCircleIcon className="w-3.5 h-3.5 text-red-500" />}
          <span className={`font-semibold ${isUnlimited ? 'text-sage-500' : getTextColor()} ${textSize}`}>
            {isUnlimited ? (
              <>
                {used} {unit}
              </>
            ) : (
              <>
                {used} / {limit} {unit}
              </>
            )}
          </span>
        </div>
      </div>

      {/* Progress bar */}
      {!isUnlimited && (
        <div className={`w-full ${getBarBg()} rounded-full ${barHeight} overflow-hidden`}>
          <div
            className={`${barHeight} rounded-full transition-all duration-500 ease-out ${getBarColor()}`}
            style={{ width: `${percentage}%` }}
          />
        </div>
      )}

      {/* Unlimited indicator */}
      {isUnlimited && (
        <div className={`w-full bg-sage-100 rounded-full ${barHeight}`}>
          <div className={`${barHeight} rounded-full bg-emerald-300 w-full opacity-40`} />
        </div>
      )}

      {/* Warning / status text */}
      {showWarning && !isUnlimited && (
        <div className="mt-1">
          {percentage >= 100 && (
            <div className="flex items-center space-x-1">
              <XCircleIcon className="w-3 h-3 text-red-500" />
              <span className="text-[10px] text-red-600 font-medium">Limit reached. Upgrade to continue.</span>
            </div>
          )}
          {percentage >= 90 && percentage < 100 && (
            <div className="flex items-center space-x-1">
              <ExclamationTriangleIcon className="w-3 h-3 text-red-400" />
              <span className="text-[10px] text-red-500">
                {remaining} {unit || 'remaining'} &mdash; approaching limit
              </span>
            </div>
          )}
          {percentage >= 70 && percentage < 90 && (
            <div className="flex items-center space-x-1">
              <ExclamationTriangleIcon className="w-3 h-3 text-amber-400" />
              <span className="text-[10px] text-amber-600">
                {remaining} {unit || 'remaining'}
              </span>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default UsageMeter;
